import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  TextField,
  Paper,
  Chip,
} from '@mui/material';
import {
  PlayArrow,
  School,
  Group,
  Email,
  VideoLibrary,
} from '@mui/icons-material';
import Navigation from './Navigation';

const Home: React.FC = () => {
  const [email, setEmail] = useState('');

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Email submitted:', email);
    setEmail('');
  };

  return (
    <Box>
      <Navigation />
      
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={5}>
            <Box
              component="img"
              src="/api/placeholder/400/500"
              alt="Jessica"
              sx={{
                width: '100%',
                height: 'auto',
                borderRadius: 2,
                boxShadow: 3,
              }}
            />
          </Grid>
          <Grid item xs={12} md={7}>
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              Hey, I'm Jessica.
            </Typography>
            <Typography variant="h5" component="h2" gutterBottom sx={{ color: 'text.secondary', mb: 3 }}>
              Digital Nomad English Teacher and YouTuber
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.2rem', lineHeight: 1.6, mb: 3 }}>
              I quit my job in 2015 to live in Japan and I've never gone back. I've helped people land their dream jobs by escaping the rat race and living a better life. Now, it's your turn. 🚀
            </Typography>
          </Grid>
        </Grid>
      </Container>

      {/* Recent YouTube Video Section */}
      <Box sx={{ backgroundColor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 'bold', mb: 6 }}>
            Watch my recent Youtube Video
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Card sx={{ maxWidth: 600, boxShadow: 3 }}>
              <CardMedia
                sx={{ height: 300, position: 'relative', cursor: 'pointer' }}
                image="/api/placeholder/600/300"
                title="Recent YouTube Video"
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(33, 150, 243, 0.9)',
                    borderRadius: '50%',
                    width: 80,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PlayArrow sx={{ fontSize: 40, color: 'white' }} />
                </Box>
              </CardMedia>
            </Card>
          </Box>
        </Container>
      </Box>

      {/* Additional YouTube Videos */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          {[1, 2, 3].map((video) => (
            <Grid item xs={12} md={4} key={video}>
              <Card sx={{ boxShadow: 2, cursor: 'pointer', '&:hover': { boxShadow: 4 } }}>
                <CardMedia
                  sx={{ height: 200, position: 'relative' }}
                  image={`/api/placeholder/400/200`}
                  title={`YouTube Video ${video}`}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(33, 150, 243, 0.9)',
                      borderRadius: '50%',
                      width: 60,
                      height: 60,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PlayArrow sx={{ fontSize: 30, color: 'white' }} />
                  </Box>
                </CardMedia>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Programs Section */}
      <Box sx={{ backgroundColor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 'bold', mb: 6 }}>
            My Programs
          </Typography>
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', boxShadow: 3 }}>
                <CardMedia
                  sx={{ height: 250 }}
                  image="/api/placeholder/400/250"
                  title="Digital Nomad Pass"
                />
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Group sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold' }}>
                      Digital Nomad Pass
                    </Typography>
                    <Chip label="100% FREE" color="success" sx={{ ml: 2 }} />
                  </Box>
                  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                    Join my free Skool community where I provide weekly Q&As and support for aspiring digital nomads. 
                    Get access to resources, connect with like-minded people, and start your journey.
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      py: 1.5,
                      fontSize: '1.1rem',
                      textTransform: 'none',
                      fontWeight: 'bold',
                    }}
                  >
                    Join Community
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', boxShadow: 3 }}>
                <CardMedia
                  sx={{ height: 250 }}
                  image="/api/placeholder/400/250"
                  title="Go Remote Academy"
                />
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <School sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold' }}>
                      Go Remote Academy
                    </Typography>
                    <Chip label="$1200/year" color="primary" sx={{ ml: 2 }} />
                  </Box>
                  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                    Application-only access with unlimited coaching to land your dream remote job. 
                    Restricted to qualified people who are serious about making the transition.
                  </Typography>
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{
                      py: 1.5,
                      fontSize: '1.1rem',
                      textTransform: 'none',
                      fontWeight: 'bold',
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                      },
                    }}
                  >
                    Apply Now
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Email Subscription */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 6, textAlign: 'center' }}>
          <Email sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            Subscribe to my email list
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', color: 'text.secondary' }}>
            Get weekly insights, tips, and exclusive content delivered straight to your inbox.
          </Typography>
          <Box component="form" onSubmit={handleEmailSubmit} sx={{ display: 'flex', gap: 2, maxWidth: 400, mx: 'auto' }}>
            <TextField
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              sx={{
                px: 4,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
                fontSize: '1rem',
              }}
            >
              Subscribe
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Home;